import React, { useState, useCallback, createContext } from "react";
import { useToast } from "./ToastContextHelper";
import { apiBaseurl } from "../utils/ApiBaseurl";
export const FileManagerContext = createContext();

export const FileManagerProvider = ({ children }) => {
  const [folders, setFolders] = useState([]);
  const [recentFiles, setRecentFiles] = useState([]);
  const [folderFiles, setFolderFiles] = useState([]);

  const { setToastMessage } = useToast();

  const getUserFolders = useCallback(async () => {
    try {
      const response = await fetch(`${apiBaseurl}backend/api/folder-manager`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${
            JSON.parse(localStorage.getItem("authTokens")).access
          }`,
          "Content-Type": "application/json",
        },
      });

      const _folders = await response.json();
      setFolders(_folders);
    } catch (error) {
      console.error("Failed to fetch folders:", error);
    }
  }, []);

  const createUserFolder = useCallback(async (folderName) => {
    try {
      const response = await fetch(`${apiBaseurl}backend/api/folder-manager`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${
            JSON.parse(localStorage.getItem("authTokens")).access
          }`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: folderName }),
      });
      if (response.ok) {
        const createdFolder = await response.json();
        setToastMessage("Folder created successfully!");
        setFolders((prevFolders) => [...prevFolders, createdFolder]);
      } else {
        const errors = await response.json();
        errors.forEach((error) => {
          setToastMessage(error);
        });
      }
    } catch (error) {
      console.error("Error creating folder:", error);
    }
  }, []);

  const renameUserFolder = useCallback(async (folderId, folderName) => {
    try {
      const response = await fetch(`${apiBaseurl}backend/api/folder-manager`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${
            JSON.parse(localStorage.getItem("authTokens")).access
          }`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ folderId: folderId, name: folderName }),
      });
      if (response.ok) {
        const updatedFolder = await response.json();
        setFolders((prevFolders) => {
          return prevFolders.map((folder) =>
            folder.id === updatedFolder.id ? updatedFolder : folder
          );
        });
        setToastMessage("Folder renamed successfully!");
      } else {
        const errors = await response.json();
        errors.forEach((error) => {
          setToastMessage(error);
        });
      }
    } catch (error) {
      console.error("Error renaming folder:", error);
    }
  }, []);

  const uploadFiles = useCallback(async (acceptedFiles, folderId) => {
    try {
      const formData = new FormData();
      for (let file of acceptedFiles) {
        formData.append("file", file);
      }
      formData.append("folder", folderId);
      const response = await fetch(`${apiBaseurl}backend/api/file-manager`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${
            JSON.parse(localStorage.getItem("authTokens")).access
          }`,
        },
        body: formData,
      });
      if (response.ok) {
        const updatedFolder = await response.json();
        await getUserFolders();
        await getRecentFiles()
        setToastMessage("Files uploaded successfully!");
      } else {
        const errors = await response.json();
        errors.forEach((error) => {
          setToastMessage(error);
        });
      }
    } catch (error) {
      console.error("Error uploading files:", error);
    }
  }, []);

  const getRecentFiles = useCallback(async () => {
    try {
      const response = await fetch(`${apiBaseurl}backend/api/recent-files`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${
            JSON.parse(localStorage.getItem("authTokens")).access
          }`,
          "Content-Type": "application/json",
        },
      });
      const recentFiles = await response.json();
      setRecentFiles(recentFiles);
    } catch (error) {
      console.error("Error getting recent files:", error);
    }
  }, []);

  const getFolderFiles = useCallback(async (folderId, setLoading) => {
    try {
      const response = await fetch(`${apiBaseurl}backend/api/folder-files`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${
            JSON.parse(localStorage.getItem("authTokens")).access
          }`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ folder_id: folderId }),
      });
      const folderFiles = await response.json();
      setFolderFiles(folderFiles);
      setLoading(false)
    } catch (error) {
      console.error("Error getting folder files:", error);
    }
  }, []);

  return (
    <FileManagerContext.Provider
      value={{
        folders,
        recentFiles,
        folderFiles,
        getFolderFiles,
        getRecentFiles,
        setFolders,
        getUserFolders,
        createUserFolder,
        renameUserFolder,
        uploadFiles,
      }}
    >
      {children}
    </FileManagerContext.Provider>
  );
};
